/**
 * Payment Proofs API
 * RTK Query endpoints for payment proof management
 */

import { baseApi } from "./baseApi";

/**
 * Payment Proof interface matching backend
 */
export interface PaymentProof {
	id: string;
	organization_id?: string;
	property_request_id: string;
	property_id: string;
	property_name?: string;
	property_address?: string;
	user_id?: string;
	user_name?: string;
	request_type?: "lease" | "buy";
	amount: number;
	payment_date: string;
	payment_method: string;
	transaction_reference?: string;
	notes?: string;
	proof_document_url: string;
	status: "pending" | "approved" | "rejected";
	reviewed_by_id?: string;
	reviewed_at?: string;
	review_notes?: string;
	created_at: string;
	updated_at: string;
}

/**
 * Payment proof list response interface
 */
export interface PaymentProofListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: PaymentProof[];
}

/**
 * Create payment proof request interface
 */
export interface PaymentProofData {
	property_request_id: string;
	amount: number;
	payment_date: string;
	payment_method: string;
	transaction_reference?: string;
	notes?: string;
	proof_document_file: string; // Base64 encoded file
}

/**
 * Update payment proof request interface
 */
export interface PaymentProofUpdateData {
	status?: "pending" | "approved" | "rejected";
	review_notes?: string;
}

/**
 * Payment Proofs API endpoints
 */
export const paymentProofsApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Create a new payment proof (public endpoint)
		 */
		createPaymentProof: builder.mutation<PaymentProof, PaymentProofData>({
			query: (data) => ({
				url: "/payment-proofs/",
				method: "POST",
				body: data,
			}),
			invalidatesTags: ["PaymentProofs"],
		}),

		/**
		 * List all payment proofs (admin only)
		 */
		listPaymentProofs: builder.query<
			PaymentProofListResponse,
			{
				status?: string;
				property_request_id?: string;
				property_id?: string;
				search?: string;
				ordering?: string;
				page?: number;
				page_size?: number;
			}
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				if (params.status) queryParams.append("status", params.status);
				if (params.property_request_id)
					queryParams.append("property_request_id", params.property_request_id);
				if (params.property_id)
					queryParams.append("property_id", params.property_id);
				if (params.search) queryParams.append("search", params.search);
				if (params.ordering) queryParams.append("ordering", params.ordering);
				if (params.page) queryParams.append("page", params.page.toString());
				if (params.page_size)
					queryParams.append("page_size", params.page_size.toString());

				const queryString = queryParams.toString();
				return {
					url: queryString
						? `/payment-proofs/?${queryString}`
						: `/payment-proofs/`,
					method: "GET",
				};
			},
			providesTags: ["PaymentProofs"],
		}),

		/**
		 * Get payment proof details by ID (admin only)
		 */
		getPaymentProof: builder.query<PaymentProof, string>({
			query: (id) => ({
				url: `/payment-proofs/${id}`,
				method: "GET",
			}),
			providesTags: (result, error, id) => [{ type: "PaymentProofs", id }],
		}),

		/**
		 * Update a payment proof (admin only) - Approve or reject
		 */
		updatePaymentProof: builder.mutation<
			PaymentProof,
			{ id: string; data: PaymentProofUpdateData }
		>({
			query: ({ id, data }) => ({
				url: `/payment-proofs/${id}`,
				method: "PATCH",
				body: data,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: "PaymentProofs", id },
				"PaymentProofs",
			],
		}),
	}),
});

export const {
	useCreatePaymentProofMutation,
	useListPaymentProofsQuery,
	useLazyListPaymentProofsQuery,
	useGetPaymentProofQuery,
	useLazyGetPaymentProofQuery,
	useUpdatePaymentProofMutation,
} = paymentProofsApi;
